"use client";

import * as React from "react";
import { formatPrice } from "../../lib/formatPrice";
import { cn } from "../../lib/utils";
import {
  PRICE_TRENDS_HEATMAP_GRID_HUE_MAX,
  PRICE_TRENDS_HEATMAP_GRID_HUE_MIN,
  PRICE_TRENDS_HEATMAP_GRID_STYLES,
} from "./constants";
import type { PriceTrendsHeatmapGridProps } from "./types";

function tintFromIntensity(t: number): string {
  const clamped = Math.min(1, Math.max(0, t));
  const hue =
    PRICE_TRENDS_HEATMAP_GRID_HUE_MIN +
    (PRICE_TRENDS_HEATMAP_GRID_HUE_MAX - PRICE_TRENDS_HEATMAP_GRID_HUE_MIN) *
      clamped;
  const lightness = 95 - clamped * 35;
  return `hsl(${hue}, 75%, ${lightness}%)`;
}

export const PriceTrendsHeatmapGrid = React.forwardRef<
  HTMLElement,
  PriceTrendsHeatmapGridProps
>(
  (
    {
      className,
      title,
      description,
      items,
      currency = "USD",
      columns = 4,
      cheapestLabel = "Más barato",
      mostExpensiveLabel = "Más caro",
      ...props
    },
    ref
  ) => {
    const { minId, maxId, min, max } = React.useMemo(() => {
      if (items.length === 0) {
        return { minId: null, maxId: null, min: 0, max: 0 };
      }
      let cheapest = items[0];
      let priciest = items[0];
      for (const item of items) {
        if (item.price < cheapest.price) cheapest = item;
        if (item.price > priciest.price) priciest = item;
      }
      return {
        minId: cheapest.id,
        maxId: priciest.id,
        min: cheapest.price,
        max: priciest.price,
      };
    }, [items]);

    const range = max - min;

    return (
      <section
        ref={ref}
        className={cn(PRICE_TRENDS_HEATMAP_GRID_STYLES.root, className)}
        {...props}
      >
        {(title || description) && (
          <header className={PRICE_TRENDS_HEATMAP_GRID_STYLES.header}>
            {title && (
              <h2 className={PRICE_TRENDS_HEATMAP_GRID_STYLES.title}>
                {title}
              </h2>
            )}
            {description && (
              <p className={PRICE_TRENDS_HEATMAP_GRID_STYLES.description}>
                {description}
              </p>
            )}
          </header>
        )}
        <div
          role="list"
          className={cn(
            PRICE_TRENDS_HEATMAP_GRID_STYLES.grid,
            PRICE_TRENDS_HEATMAP_GRID_STYLES.gridCols[columns]
          )}
        >
          {items.map((item) => {
            const t = range > 0 ? (item.price - min) / range : 0;
            const isMin = item.id === minId;
            const isMax = item.id === maxId;
            const formattedPrice = formatPrice(item.price, currency);
            return (
              <div
                key={item.id}
                role="listitem"
                data-testid={`price-trends-heatmap-grid-cell-${item.id}`}
                data-variant={isMin ? "min" : isMax ? "max" : "default"}
                className={PRICE_TRENDS_HEATMAP_GRID_STYLES.cell}
                style={{ backgroundColor: tintFromIntensity(t) }}
              >
                {(isMin || isMax) && (
                  <span
                    className={cn(
                      PRICE_TRENDS_HEATMAP_GRID_STYLES.ribbon,
                      isMin
                        ? PRICE_TRENDS_HEATMAP_GRID_STYLES.ribbonMin
                        : PRICE_TRENDS_HEATMAP_GRID_STYLES.ribbonMax
                    )}
                  >
                    {isMin ? cheapestLabel : mostExpensiveLabel}
                  </span>
                )}
                <span className={PRICE_TRENDS_HEATMAP_GRID_STYLES.cellMonth}>
                  {item.month}
                </span>
                <span className={PRICE_TRENDS_HEATMAP_GRID_STYLES.cellPrice}>
                  {formattedPrice}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
);

PriceTrendsHeatmapGrid.displayName = "PriceTrendsHeatmapGrid";
